import React, { useDeferredValue, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { FiSearch, FiX, FiUserPlus } from 'react-icons/fi';
import PageLayout from '../../components/layout/PageLayout';
import ProfileIdentity from '../../components/content/ProfileIdentity';
import PlainImage from '../images/PlainImage';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { AccountBoundary, SettingsHeader, useSettings } from '../settings/shared';
import { saveCurrentPathAndRedirectToLogin } from '../../utils/authUtils';
import { restoreSearchScroll } from '../search/useSearchScrollMemory';
import { useMemberEntry } from './memberMemory';
import { memberReason } from './memberModel';
import useMemberDiscovery from './useMemberDiscovery';
import useMemberTracking from './useMemberTracking';
import copy from './copy.json';
import '../../pages/mypage/components/FollowListDialog.css';
import './UserDiscovery.css';
export default function UserDiscoveryPage() {
  return <AccountBoundary component={DiscoveryBoundary} />;
}
function DiscoveryBoundary({ user }) {
  const { locale } = useWebLocale(),
    { style } = useSettings(),
    navigate = useNavigate();
  const t = (key, values = {}) =>
    (copy[locale === 'en' ? 'en' : 'ko'][key] || key).replace(
      /\{\{(\w+)\}\}/g,
      (_, k) => values[k] ?? '',
    );
  const { entry, returning, location } = useMemberEntry('discovery');
  return (
    <PageLayout paddingTop={0}>
      <section className="pp-finder-discovery pp-settings" style={style}>
        <SettingsHeader title={t('profile.reviewerFinderTitle')} />
        {user && localStorage.getItem('POSTPOP_AUTH_TOKEN') ? (
          <Discovery
            key={`${location.key}:${entry.version}`}
            {...{ user, entry, returning, t, locale }}
          />
        ) : (
          <div className="pp-finder-empty">
            <p>{locale === 'en' ? 'Please log in to continue.' : '로그인이 필요해요.'}</p>
            <button onClick={() => saveCurrentPathAndRedirectToLogin(navigate)}>
              {locale === 'en' ? 'Log in' : '로그인'}
            </button>
          </div>
        )}
      </section>
    </PageLayout>
  );
}
function Discovery({ user, entry, returning, t, locale }) {
  const navigate = useNavigate(),
    input = useRef(null);
  const [query, setQuery] = useState(returning ? entry.query || '' : ''),
    [composing, setComposing] = useState(false),
    [committed, setCommitted] = useState(returning ? entry.query || '' : '');
  const deferred = useDeferredValue((composing ? committed : query).trim());
  const model = useMemberDiscovery(user, deferred, entry),
    { items, list, recommended } = model;
  const tracking = useMemberTracking(entry, user, items, recommended);
  const more = useRef(null),
    restore = useRef(returning),
    restoreY = useRef(entry.y || 0);
  const rowSet = items.map((u) => u.id).join(',');
  const ready = deferred.length === 1 || !list.isPending;
  useEffect(() => {
    if (!restore.current || !ready) return;
    let cancel;
    const timer = setTimeout(() => {
      if (!entry.current()) return;
      cancel = restoreSearchScroll(restoreY.current, () => {
        restore.current = false;
        if (entry.current() && entry.focus)
          document
            .querySelector(`[data-member-id="${Number(entry.focus)}"] .pp-follow-identity`)
            ?.focus({ preventScroll: true });
      });
    }, 0);
    return () => {
      clearTimeout(timer);
      cancel?.();
    };
  }, [entry, ready]);
  useEffect(() => {
    const save = () => {
      if (entry.current() && !restore.current) entry.y = window.scrollY;
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [entry]);
  useEffect(() => {
    entry.query = query;
  }, [entry, query]);
  useEffect(() => {
    if (
      !more.current ||
      list.isError ||
      !list.hasNextPage ||
      list.isFetching ||
      typeof IntersectionObserver === 'undefined'
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !document.hidden) model.loadMore();
      },
      { rootMargin: '320px' },
    );
    observer.observe(more.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSet, list.isError, list.hasNextPage, list.isFetching, deferred]);
  const change = (value) => {
    setQuery(value.slice(0, 14));
    entry.y = 0;
    entry.focus = null;
  };
  const open = (item, index) => {
    tracking.click(item, index);
    entry.query = query;
    entry.y = window.scrollY;
    entry.focus = item.id;
    navigate(`/users/${encodeURIComponent(item.nickname)}`);
  };
  const en = locale === 'en',
    errorText = t('profile.usersLoadError');
  return (
    <>
      <div className="pp-finder-search">
        <FiSearch size={18} />
        <input
          ref={input}
          aria-label={t('profile.reviewerSearchA11y')}
          placeholder={t('profile.reviewerSearchPlaceholder')}
          value={query}
          maxLength={14}
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          onChange={(e) => change(e.target.value)}
          onCompositionStart={() => {
            setCommitted(query);
            setComposing(true);
          }}
          onCompositionEnd={(e) => {
            setComposing(false);
            setCommitted(e.currentTarget.value);
          }}
        />
        {query && (
          <button
            aria-label={en ? 'Clear search' : '검색어 지우기'}
            onClick={() => {
              change('');
              setCommitted('');
              input.current?.focus();
            }}
          >
            <FiX size={17} />
          </button>
        )}
      </div>
      {deferred.length === 1 ? (
        <div className="pp-finder-empty">
          <FiUserPlus size={28} />
          <p>{t('profile.moreSearchCharacters')}</p>
        </div>
      ) : (
        <>
          {list.isPending && (
            <div role="status" aria-label={en ? 'Loading' : '불러오는 중'}>
              <CircularProgress size={24} />
            </div>
          )}
          {!list.isPending && !list.isError && !items.length && (
            <p className="pp-finder-empty">
              {t(recommended ? 'profile.noRecommendedUsers' : 'profile.noMatchingUsers')}
            </p>
          )}
          {!!items.length && (
            <div className="pp-finder-heading">
              <h2>{t(recommended ? 'profile.recommendedUsers' : 'profile.searchResults')}</h2>
              {recommended && <p>{t('profile.activeReviewers')}</p>}
            </div>
          )}
          {model.mutationError && (
            <p role="alert">
              {en
                ? 'Could not update following. Try again.'
                : '팔로우 상태를 변경하지 못했어요. 다시 시도해 주세요.'}
            </p>
          )}
          <div className="pp-finder-results">
            {items.map((member, index) => (
              <div
                className="pp-follow-row"
                key={member.id}
                data-member-id={member.id}
                ref={(node) => tracking.rowRef(member.id, node)}
              >
                <button
                  className="pp-follow-identity"
                  aria-label={
                    en ? `Open ${member.nickname}’s profile` : `${member.nickname} 프로필 보기`
                  }
                  onClick={() => open(member, index)}
                >
                  <span className="pp-follow-avatar" aria-hidden="true">
                    {member.profileImageUrl ? (
                      <PlainImage src={member.profileImageUrl} alt="" />
                    ) : (
                      member.nickname?.trim()[0]?.toUpperCase() || 'P'
                    )}
                  </span>
                  <span className="pp-follow-copy">
                    <span className="pp-follow-name">
                      <ProfileIdentity
                        name={member.nickname}
                        mark={member.profileMark}
                        avatar={false}
                      />
                    </span>
                    <span className="pp-follow-bio">
                      {recommended
                        ? memberReason(member, t)
                        : t('profile.followersCount', { count: member.followersCount ?? 0 })}
                    </span>
                  </span>
                </button>
                {member.id !== user.id && (
                  <button
                    className={`pp-follow-action${member.isFollowing ? ' is-following' : ''}`}
                    aria-label={`${member.nickname} ${t(member.isFollowing ? 'profile.unfollow' : 'profile.follow')}`}
                    disabled={model.busy !== null}
                    onClick={() => {
                      tracking.click(member, index, true);
                      void model.toggle(member);
                    }}
                  >
                    {model.busy === member.id ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      t(member.isFollowing ? 'profile.followingAction' : 'profile.follow')
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
          {list.isFetchingNextPage && (
            <div role="status" aria-label={en ? 'Loading more' : '더 불러오는 중'}>
              <CircularProgress size={24} />
            </div>
          )}
          {list.isError && (
            <div role="alert" className="pp-finder-error">
              <p>{errorText}</p>
              <button disabled={list.isFetching} onClick={model.retry}>
                {en ? 'Try again' : '다시 시도'}
              </button>
            </div>
          )}
          {list.hasNextPage && !list.isError && (
            <button
              ref={more}
              className="pp-finder-more"
              disabled={list.isFetching}
              onClick={model.loadMore}
            >
              {en ? 'Show more' : '더 보기'}
            </button>
          )}
        </>
      )}
    </>
  );
}
